import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Button } from '../components/ui';

type OrderApprovalMode = 'ADMIN_ONLY' | 'SELLER_ALLOWED';

const Settings: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const [orderApprovalMode, setOrderApprovalMode] = useState<OrderApprovalMode>('ADMIN_ONLY');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/platform');
      const mode = response.data?.data?.orderApprovalMode as OrderApprovalMode;
      if (mode === 'ADMIN_ONLY' || mode === 'SELLER_ALLOWED') {
        setOrderApprovalMode(mode);
      }
    } catch (error) {
      console.error('Failed to load platform settings', error);
      setMessage(t('orderManagement.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.patch('/admin/settings/platform', { orderApprovalMode });
      setMessage(t('orderManagement.saved'));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setMessage(err.response?.data?.message || t('orderManagement.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const isSellerAllowed = orderApprovalMode === 'SELLER_ALLOWED';

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-600">{t('loading', { ns: 'common', defaultValue: 'Loading...' })}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('title')}</h1>
      <p className="text-gray-600 mb-8">{t('subtitle')}</p>

      <section className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('orderManagement.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('orderManagement.description')}</p>
          </div>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
              isSellerAllowed
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {isSellerAllowed
              ? t('orderManagement.statusEnabled')
              : t('orderManagement.statusDisabled')}
          </span>
        </div>

        <p className="text-sm font-medium text-gray-700 mb-3">{t('orderManagement.chooseMode')}</p>

        <div className="space-y-3">
          <label
            className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              !isSellerAllowed ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="orderApprovalMode"
              className="mt-1"
              checked={!isSellerAllowed}
              onChange={() => setOrderApprovalMode('ADMIN_ONLY')}
            />
            <span>
              <span className="block font-medium text-gray-900">
                {t('orderManagement.modeAdminOnly')}
              </span>
              <span className="block text-sm text-gray-600 mt-1">
                {t('orderManagement.adminOnlyHint')}
              </span>
            </span>
          </label>

          <label
            className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              isSellerAllowed ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="orderApprovalMode"
              className="mt-1"
              checked={isSellerAllowed}
              onChange={() => setOrderApprovalMode('SELLER_ALLOWED')}
            />
            <span>
              <span className="block font-medium text-gray-900">
                {t('orderManagement.modeSellerAllowed')}
              </span>
              <span className="block text-sm text-gray-600 mt-1">
                {t('orderManagement.sellerAllowedHint')}
              </span>
            </span>
          </label>
        </div>

        <p className="text-xs text-gray-500 mt-4">{t('orderManagement.saveReminder')}</p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('orderManagement.saving') : t('orderManagement.save')}
          </Button>
          {message && (
            <p
              className={`text-sm ${
                message === t('orderManagement.saved') ? 'text-green-700' : 'text-red-600'
              }`}
            >
              {message}
            </p>
          )}
        </div>
      </section>

      <section className="mt-6 bg-gray-50 rounded-lg p-4 text-sm text-gray-700 border border-gray-200">
        <p className="font-medium mb-2">{t('orderManagement.whereToSeeTitle')}</p>
        <ul className="list-disc list-inside space-y-1">
          <li>{t('orderManagement.whereAdmin')}</li>
          <li>{t('orderManagement.whereSeller')}</li>
        </ul>
      </section>
    </div>
  );
};

export default Settings;
